import { Button } from '@synoptic/ui-kit/button/button.tsx';
import { Checkbox } from '@synoptic/ui-kit/checkbox/checkbox.tsx';
import { Input } from '@synoptic/ui-kit/input/input.tsx';
import { PasswordInput } from '@synoptic/ui-kit/input/password-input.tsx';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.tsx';
import { signUp } from 'aws-amplify/auth';
import { clsx } from 'clsx';
import { FC, useRef } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useSearchParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { Link } from '@/routes-utils/navigation.tsx';
import { paths } from '@/routes-utils/paths';
import { sanitizeRedirectParam } from '@/routes-utils/sanitize-redirect-param.ts';
import { pushService } from '@/services/push-service.ts';
import { traceError } from '@/utils/trace-error.ts';
import { passwordPattern } from '../../form-utils/password-pattern';
import {
  actionLink,
  actionLinkBlock,
  cardBody,
  checkbox,
  formFieldsContainer,
  formHeading,
  submitAuthFormButton,
} from '../_auth/page.css';

type FormInputs = {
  name: string;
  email: string;
  password: string;
  invitationCode: string;
  policy: boolean;
};

export const SignupForm: FC<{
  onSignup: (username: string, email: string) => void;
  inviteCodeQueryParam: string | null;
}> = ({ onSignup, inviteCodeQueryParam }) => {
  const [searchParams] = useSearchParams();
  const fromParam = sanitizeRedirectParam(searchParams.get('from'));
  const oauthParam = searchParams.get('oauth') ?? undefined;

  const { handleSubmit, formState, reset, register, setValue, control } =
    useForm<FormInputs>({
      defaultValues: {
        invitationCode: inviteCodeQueryParam ?? undefined,
        policy: false,
      },
    });
  const formRef = useRef<HTMLFormElement>(null);
  const toast = useToast();
  const policy = useWatch({ control, name: 'policy' });

  const submit: SubmitHandler<FormInputs> = async (data) => {
    const { name, password, email } = data;
    const username = uuidv4();

    try {
      const subscription = await pushService.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }
    } catch (err) {
      traceError(
        new Error('Failed to subscribe on push notifications on signup', {
          cause: err,
        }),
      );
    }

    try {
      await signUp({
        username,
        password,
        options: {
          userAttributes: { email, name },
          autoSignIn: true,
        },
      });
      window.mixpanel?.track('Sign Up', {
        username,
        email,
      });

      onSignup(username, email);
      reset();
    } catch (e) {
      toast.error({ title: String(e) });
    }
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit(submit)}>
      <h1 className={formHeading}>Create your account</h1>
      <p className={cardBody}>
        Welcome! Please fill in the details to get started.
      </p>
      <div className={formFieldsContainer}>
        <Input
          {...register('name', {
            required: true,
            maxLength: {
              value: 50,
              message: 'Name cannot exceed 50 characters',
            },
          })}
          maxLength={50}
          errorMessage={formState.errors.name?.message}
          placeholder={
            'Enter your profile name (will be visible to other users)'
          }
          label={'Name'}
          autoComplete="name"
        />
        <Input
          {...register('email', {
            required: true,
          })}
          placeholder={'Enter your email'}
          type="email"
          label={'Email'}
          autoComplete="username"
        />
        <PasswordInput
          {...register('password', {
            required: true,
            pattern: passwordPattern,
          })}
          placeholder={'Enter your password'}
          errorMessage={formState.errors.password?.message}
          label={'Password'}
          autoComplete="new-password"
        />
        <label className={checkbox}>
          <Checkbox
            {...register('policy')}
            onCheckedChange={(checked: boolean) => setValue('policy', checked)}
          />
          <span>
            I agree to the{' '}
            <Link
              className={actionLink}
              to={paths.termsOfUse}
              target={'_blank'}
            >
              Terms and Conditions
            </Link>{' '}
            and{' '}
            <Link
              className={actionLink}
              to={paths.privacyPolicy}
              target={'_blank'}
            >
              Privacy Policy
            </Link>
            .
          </span>
        </label>

        <Button
          className={submitAuthFormButton}
          size={'large'}
          type="submit"
          disabled={!policy}
          loading={formState.isSubmitting}
        >
          Continue
        </Button>
      </div>
      <p className={clsx(cardBody, actionLinkBlock)}>
        Already have an account?{' '}
        <Link
          className={actionLink}
          to={paths.makeLogin(fromParam, oauthParam)}
        >
          Log in
        </Link>
      </p>
    </form>
  );
};
